import React, { useEffect, useState, useCallback } from 'react';
import Button from '../../../components/ui/Button';
import Icon from '../../../components/AppIcon';
import ConfirmDialog from '../../../components/ui/ConfirmDialog';
import { useToast } from '../../../components/ui/Toast';
import { platformDefinitionService, platformConsumerService } from '../../../services/apiService';
import { parseApiError } from '../apiErrors';
import { PageHeader, StatusBadge, ErrorBanner, EmptyState } from '../_components/common';
import DefinitionWizard from './DefinitionWizard';
import ActivationSecretModal from './ActivationSecretModal';

const Th = ({ children, className = '' }) => <th className={`text-left font-semibold px-4 py-3 ${className}`}>{children}</th>;
const Td = ({ children, className = '' }) => <td className={`px-4 py-3 align-top ${className}`}>{children}</td>;

const PlatformDefinitions = () => {
  const toast = useToast();
  const [data, setData] = useState([]);
  const [consumerNames, setConsumerNames] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [wizard, setWizard] = useState(false);
  const [creds, setCreds] = useState(null); // one-time activation secret
  const [confirm, setConfirm] = useState(null); // { type: 'activate' | 'revoke', def }
  const [actingId, setActingId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [defs, cons] = await Promise.all([
        platformDefinitionService.list({ page: 1, limit: 100 }),
        platformConsumerService.list({ page: 1, limit: 100 }),
      ]);
      setData(defs.data || []);
      const map = {};
      (cons.data || []).forEach((c) => { map[c.id] = c.name; });
      setConsumerNames(map);
    } catch (err) {
      setError(parseApiError(err));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const doActivate = async (def) => {
    setActingId(def.id);
    try {
      const res = await platformDefinitionService.activate(def.id);
      setCreds(res.data); // { apiKey, apiSecret, warning }
      await load();
    } catch (err) {
      toast.error(parseApiError(err).message);
    } finally {
      setActingId(null);
      setConfirm(null);
    }
  };

  const doRevoke = async (def) => {
    setActingId(def.id);
    try {
      await platformDefinitionService.revoke(def.id);
      toast.success('Definition revoked');
      await load();
    } catch (err) {
      toast.error(parseApiError(err).message);
    } finally {
      setActingId(null);
      setConfirm(null);
    }
  };

  return (
    <div className="max-w-6xl">
      <PageHeader
        title="API Definitions"
        subtitle="Scoped read APIs: create a draft, activate to mint the credential (secret shown once), revoke when done. No edits — revoke and recreate to change scope."
        actions={<Button iconName="Plus" onClick={() => setWizard(true)}>New API</Button>}
      />
      <ErrorBanner error={error} onClose={() => setError(null)} />

      {loading ? (
        <div className="py-16 text-center text-slate-400"><Icon name="Loader" className="animate-spin inline mr-2" size={18} />Loading…</div>
      ) : data.length === 0 ? (
        <EmptyState icon="KeyRound" title="No API definitions yet" text="Create one to expose scoped, field-filtered reads to a consumer."
          action={<Button iconName="Plus" onClick={() => setWizard(true)}>New API</Button>} />
      ) : (
        <div className="bg-white border border-slate-200 rounded-xl overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-500 text-xs uppercase tracking-wide border-b border-slate-200">
              <tr><Th>Name</Th><Th>Consumer</Th><Th>Entity</Th><Th>Fields</Th><Th>Status</Th><Th>API key</Th><Th className="text-right">Actions</Th></tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {data.map((d) => (
                <tr key={d.id} className="hover:bg-slate-50">
                  <Td><div className="font-medium text-slate-900">{d.name}</div></Td>
                  <Td className="text-slate-600">{consumerNames[d.consumerId] || <span className="font-mono text-xs">{String(d.consumerId).slice(0, 8)}…</span>}</Td>
                  <Td className="text-slate-600">{d.entity}</Td>
                  <Td className="text-slate-600">{d.selectedFieldIds?.length ?? 0}</Td>
                  <Td><StatusBadge status={d.status} /></Td>
                  <Td>{d.apiKey ? <code className="text-xs font-mono text-slate-500">{d.apiKey}</code> : <span className="text-slate-300">—</span>}</Td>
                  <Td className="text-right whitespace-nowrap">
                    {d.status === 'draft' && (
                      <Button variant="success" size="sm" iconName="Zap" loading={actingId === d.id} onClick={() => setConfirm({ type: 'activate', def: d })}>Activate</Button>
                    )}
                    {d.status === 'active' && (
                      <Button variant="ghost" size="sm" iconName="Ban" loading={actingId === d.id} onClick={() => setConfirm({ type: 'revoke', def: d })}>Revoke</Button>
                    )}
                    {d.status === 'revoked' && <span className="text-xs text-slate-400 px-2">Revoked</span>}
                  </Td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {wizard && <DefinitionWizard onClose={() => setWizard(false)} onCreated={() => { setWizard(false); load(); }} />}
      {creds && <ActivationSecretModal creds={creds} onAcknowledge={() => setCreds(null)} />}

      <ConfirmDialog
        isOpen={!!confirm}
        onClose={() => setConfirm(null)}
        onConfirm={() => confirm && (confirm.type === 'activate' ? doActivate(confirm.def) : doRevoke(confirm.def))}
        title={confirm?.type === 'activate' ? 'Activate API definition?' : 'Revoke API definition?'}
        message={
          confirm?.type === 'activate'
            ? `Activating "${confirm?.def?.name}" generates its API key and a secret. The secret is shown only once — have it ready to copy.`
            : `Revoking "${confirm?.def?.name}" permanently disables its credential. There is no un-revoke; you'd create a new definition to restore access.`
        }
        confirmText={confirm?.type === 'activate' ? 'Activate' : 'Revoke'}
        variant={confirm?.type === 'activate' ? 'info' : 'danger'}
        loading={actingId === confirm?.def?.id}
      />
    </div>
  );
};

export default PlatformDefinitions;
